import { STRINGS } from '@/constants/strings';
import type { EditablePlayerGender, PlayerGenderFormValue } from '@/utils/player-gender';
import { birthDateRule, cityRule, nameRule, zipCodeRule } from '@/validation';
import * as yup from 'yup';

const MESSAGES = STRINGS.mutatePlayer.validation;

/** 10MB, in bytes — profile photo upper bound (PP-376 BR-07). */
const MAX_AVATAR_BYTES = 10 * 1024 * 1024;
/** Exact BR-07 copy (the shared `mutatePlayer.validation` string omits the trailing period). */
const AVATAR_TOO_LARGE_MESSAGE = 'Your image exceeds 10MB. Please upload another image.';

export type PlayerGender = EditablePlayerGender;
export type DominantHand = 'left' | 'right';

export const GENDER_OPTIONS: { value: PlayerGender; label: string }[] = [
  { value: 'male', label: STRINGS.onboarding.addPlayer.genderMale },
  { value: 'female', label: STRINGS.onboarding.addPlayer.genderFemale },
];

/**
 * Guardian "Add Player" form (PP-376). Only name/DOB/gender are required; the whole address
 * section and the photo are optional. Distinct from the onboarding `addPlayerSchema`, which
 * also requires height + avatar — fields this screen intentionally doesn't show.
 *
 * `country` is UI-only (drives the State/Province input mode) and isn't submitted.
 */
export const guardianAddPlayerSchema = yup
  .object({
    firstName: nameRule(MESSAGES.fieldRequired),
    lastName: nameRule(MESSAGES.fieldRequired),
    dob: birthDateRule(MESSAGES.fieldRequired, MESSAGES.birthDateCannotBeFuture),
    gender: yup
      .mixed<PlayerGenderFormValue>()
      .oneOf(['male', 'female'], MESSAGES.fieldRequired)
      .required(MESSAGES.fieldRequired),
    // Shown only in edit mode (not in the PP-376 create design).
    height: yup.string().trim().defined(),
    dominantHand: yup.mixed<DominantHand>().oneOf(['left', 'right']).defined(),
    sameAddress: yup.boolean().defined(),
    country: yup.string().trim().defined().nullable(),
    state: yup.string().trim().defined().nullable(),
    city: cityRule(),
    zipCode: zipCodeRule(),
    avatar: yup
      .object({
        uri: yup.string().defined(),
        fileSize: yup.number().optional(),
      })
      .nullable()
      .test('avatar-size', AVATAR_TOO_LARGE_MESSAGE, value => {
        if (!value?.fileSize) return true;
        return value.fileSize <= MAX_AVATAR_BYTES;
      }),
  })
  .required();

export type GuardianAddPlayerForm = {
  firstName: string;
  lastName: string;
  dob: Date | null;
  gender: PlayerGenderFormValue;
  height: string;
  dominantHand: DominantHand;
  sameAddress: boolean;
  country: string | null;
  state: string | null;
  city: string;
  zipCode: string;
  avatar?: { uri: string; fileSize?: number } | null;
};

export const INITIAL_GUARDIAN_ADD_PLAYER_FORM: GuardianAddPlayerForm = {
  firstName: '',
  lastName: '',
  dob: null,
  gender: '',
  height: '',
  dominantHand: 'right',
  sameAddress: false,
  country: null,
  state: null,
  city: '',
  zipCode: '',
  avatar: null,
};
